import { ISignupApiResponseProps, signupUser } from '../api/signup';
import { ISocialLogInApiResponseProps, socialLogIn } from '../api/social';
import {
    GA_EVENTBRITE,
    GA_PASSWORDLESS,
    GA_SIGNUP_EVENT,
} from '../constants/analytics';
import {
    IAllContextsProps,
    IFormikActions,
    ISignupFormProps,
    ISocialSignupActionData,
} from '../constants/interfaces';
import { trackGeneralPageUnloadAuthAction } from '../tracking/general';
import { trackLoginSuccessful } from '../tracking/login';
import {
    trackFBLoginSuccess,
    trackSocialSignupEvent,
} from '../tracking/social';
import { parseFormPostData } from '../utils';
import { getNewUserRedirectUrl, getSignupFormData } from '../utils/signup';
import { getSocialSignUpFormData } from '../utils/social';
import { handleOnErrorLogin } from './loginErrors';
import { handleSocialLogInErrors } from './socialErrors';

export const signupUserAction = ({
    formData,
    contexts,
    formikActions,
}: {
    formData: ISignupFormProps;
    contexts: IAllContextsProps;
    formikActions: IFormikActions;
}) => {
    contexts.frontendContext?.methods.setSignupOngoing(true);
    const verificationCode = contexts.userContext?.state?.verificationCode;
    const useVerificationCode =
        contexts.configContext?.featureFlags?.launchSignupWithVerificationCode;

    return signupUser({
        data: parseFormPostData(getSignupFormData({ formData, contexts })),
        useVerificationCode,
        verificationCode,
    })
        .then((response: ISignupApiResponseProps) => {
            const redirectUrl = getNewUserRedirectUrl({
                configContext: contexts.configContext,
            });

            const _response = {
                ...response,
                redirectUrl,
            };
            const passwordlessCreateAccountFlow =
                contexts.frontendContext?.state.passwordlessCreateAccountFlow;

            trackGeneralPageUnloadAuthAction({
                analytics: contexts.configContext?.analytics,
                label: passwordlessCreateAccountFlow
                    ? GA_PASSWORDLESS
                    : GA_EVENTBRITE,
                action: GA_SIGNUP_EVENT,
            });

            trackLoginSuccessful({
                analytics: contexts.configContext?.analytics,
            });

            contexts.configContext?.signupMethods?.success?.(_response);
        })
        .catch((error: any) => {
            handleOnErrorLogin({
                parsedError: error,
                contexts,
                formikActions,
            });
        })
        .finally(() => {
            contexts.frontendContext?.methods.setSignupOngoing(false);
        });
};

// Seems to be accessed on facebook flow only.
export const handleSocialSignupUser = ({
    formData,
    contexts,
}: {
    formData: ISocialSignupActionData;
    contexts: IAllContextsProps;
}) => {
    contexts.configContext?.socialMethods?.signup?.attempt?.(formData);
    const socialProvider = contexts.userContext?.state.socialProvider;

    return socialLogIn(
        parseFormPostData(getSocialSignUpFormData({ formData, contexts })),
    )
        .then((response: ISocialLogInApiResponseProps) => {
            const analytics = contexts.configContext?.analytics;

            trackFBLoginSuccess({
                response,
                analytics,
            });

            let redirectUrl = response.redirectUrl ?? '';
            if (response.newUser) {
                redirectUrl = getNewUserRedirectUrl({
                    redirectUrl: response.redirectUrl,
                    configContext: contexts.configContext,
                });
            }

            const _response = {
                ...response,
                redirectUrl,
            };

            trackSocialSignupEvent({ analytics });

            contexts.configContext?.socialMethods?.signup?.success?.(_response);
        })
        .catch((error: any) => {
            contexts.configContext?.socialMethods?.signup?.error?.(formData);

            handleSocialLogInErrors({
                _errorSpec: error,
                socialProvider,
                responseErrorObj: formData,
                contexts,
            });
        });
};
